import { ref, reactive, computed, watch } from 'vue'
import { defineStore } from 'pinia'
import dayjs from 'dayjs'
import utils from '@/utils/utils'
import { useDdClient } from '@/stores/due_diligence/ddClient.store'
import { defaultsOf, entriesHash } from '@/utils/dd/entryHelpers'

export const useDdEstate = defineStore('ddEstate', () => {
  const model = reactive({})
  const realEstate = ref([])
  const otherClients = ref([])
  const members = ref([])
  const entities = ref([])
  const bequests = ref([])
  const estateTransactions = ref([])
  // Prelim transactions counted only up to (and including) the current deal's advance date;
  // when no advance date is set, all transactions are counted.
  const currentAdvanceDate = ref(null)
  const ddClient = useDdClient()

  const prelimTxnsInScope = computed(() => {
    const cutoff = currentAdvanceDate.value ? dayjs(currentAdvanceDate.value) : null
    return estateTransactions.value.filter(t =>
      t.targetGroup === 'prelims' &&
      t.amount != null &&
      (!cutoff || dayjs(t.date).isBefore(cutoff.add(1, 'day'), 'day'))
    )
  })

  const liquidAssetsHash        = computed(() => entriesHash(defaultsOf(model.liquidAssets)))
  const costsAndClaimsHash      = computed(() => entriesHash(defaultsOf(model.costsAndClaims)))
  const otherAssetsHash         = computed(() => entriesHash(defaultsOf(model.otherAssets)))

  function loadData(responseData) {
    // To update the object's properties while preserving reactivity:
    Object.assign(model, responseData.ddEstate)
    if (!model.executorOne) model.executorOne = {}
    if (!model.executorTwo) model.executorTwo = {}
    realEstate.value = responseData.realEstate
    otherClients.value = responseData.otherClients
    members.value = responseData.members || []
    entities.value = responseData.entities || []
    bequests.value = responseData.bequests || []
    estateTransactions.value = responseData.estateTransactions || []
    currentAdvanceDate.value = responseData.ddDeal?.deal?.advanceDate ?? null
  }

  // --------------------------------
  // Real Estate Property Calculations - whenever realEstate ref is updated this will recalculate
  // --------------------------------
  const realEstateCalcs = computed(() => {
    // A change in realEstate.value will trigger a recalculation
    return realEstate.value?.map((property) => {
      return calculateRealEstateProperty(property)
    })
  })

  function calculateRealEstateProperty(property) {
      // Debt Entries — default rows matching debtKeys, plus all custom rows
      const debtKeys = ['loan_balance_due', 'delinquent_property_taxes', 'delinquent_loan_costs', 'other_liens', 'other_items_due']
      const debtEntries = utils.filter(property.entries, (entry) => debtKeys.includes(entry.key) || entry.isCustom)
      const filteredDebtEntries = utils.filter(debtEntries, (entry) => !entry.isExcluded)

      // Line item values
      const listingPrice         = property.listingPrice
      const salesCostRate        = property.entries.find((entry) => entry.key=='sales_cost_rate')?.val || 0
      const carryCostRate        = property.entries.find((entry) => entry.key=='carry_cost_rate')?.val || 0
      const assumedReductionRate = property.entries.find((entry) => entry.key=='assumed_reduction_rate')?.val || 0
      // Calculations for each property
      const assumedReduction    = listingPrice * (assumedReductionRate/100)
      const assumedSalesPrice   = listingPrice - assumedReduction
      const salesCost           = assumedSalesPrice * (salesCostRate/100)
      const netSalesPrice       = assumedSalesPrice - salesCost
      const totalExpenses       = utils.sumBy(filteredDebtEntries, 'val')
      const netProceedsFromSale = netSalesPrice - totalExpenses
      const carryCost           = 6 * assumedSalesPrice * (carryCostRate/100)
      const subtotal            = netProceedsFromSale - carryCost
      const fullAddress = utils.compact([property.street, property.city, utils.compact([property.state, property.zip]).join(' ')]).join(', ')

      return {
        id:         property.id,
        isExcluded: property.isExcluded,
        label:      fullAddress,
        assumedReduction,
        assumedSalesPrice,
        salesCost,
        netSalesPrice,
        totalExpenses,
        netProceedsFromSale,
        carryCost,
        subtotal,
        debtEntries,
      }
  }

  const allDebtItems = computed(() => {
    const result = {
      secured: [],
      unsecured: [],
    }
    // Real estate expenses are Secured Debt
    realEstateCalcs.value?.map((propertyCalc) => {
      result.secured.push({
        label:    propertyCalc.label,
        subtotal: utils.sumBy(propertyCalc.debtEntries, 'val'),
        items:    propertyCalc.debtEntries,
      })
    })
    // Costs & Claim expenses are Unsecured Debt
    result.unsecured.push({
      label:    'Costs & Claims Unsecured Debt',
      subtotal: utils.sumBy(costsAndClaimsCalcs.debtEntries.value, 'val'),
      items:    costsAndClaimsCalcs.debtEntries.value,
    })
    return result
  })

  const allLiquidItems = computed(() => model.liquidAssets || [])

  const allOtherItems = computed(() => model.otherAssets || [])

  // --------------------------------
  // Excluded Items
  // --------------------------------
  const excludedItems = computed(() => {
    // Filter and return excluded items
    const realItems   = utils.filter(realEstateCalcs.value, {isExcluded: true})
    const liquidItems = utils.filter(allLiquidItems.value, {isExcluded: true})
    const otherItems  = utils.filter(allOtherItems.value,  {isExcluded: true})

    return {
      excludedRealEstate:   realItems.map((item)   => { return {label: item.label, subtotal: item.subtotal} }),
      excludedLiquidAssets: liquidItems.map((item) => { return {label: item.label, subtotal: item.val} }),
      excludedOtherAssets:  otherItems.map((item)  => { return {label: item.label, subtotal: item.val} }),
    }
  })

  const hasExclusions = computed(() => {
    const boolA = excludedItems.value?.excludedRealEstate.length > 0
    const boolB = excludedItems.value?.excludedLiquidAssets.length > 0
    const boolC = excludedItems.value?.excludedOtherAssets.length > 0
    return boolA || boolB || boolC
  })

  const grandTotals = {
    //----------------------------------
    // Asset Totals (without exclusions)
    //----------------------------------
    realEstate:   computed(() => {
      const unexcluded = utils.filter(realEstateCalcs.value, (item) => !item.isExcluded) 
      return utils.sumBy(unexcluded, 'subtotal')
    }),
    liquidAssets: computed(() => {
      const unexcluded = utils.filter(allLiquidItems.value, (item) => !item.isExcluded) 
      return utils.sumBy(unexcluded, 'val')
    }),
    otherAssets:  computed(() => {
      const unexcluded = utils.filter(allOtherItems.value, (item) => !item.isExcluded) 
      return utils.sumBy(unexcluded, 'val')
    }),
    netTotalAssets: computed(() => {
      return grandTotals.realEstate.value + grandTotals.liquidAssets.value + grandTotals.otherAssets.value
    }),
    //----------------------------------
    // Excluded Totals
    //----------------------------------
    excludedRealEstate: computed(() => {
      return utils.sumBy(excludedItems.value?.excludedRealEstate, 'subtotal')
    }),
    excludedLiquidAssets: computed(() => {
      return utils.sumBy(excludedItems.value?.excludedLiquidAssets, 'subtotal')
    }),
    excludedOtherAssets: computed(() => {
      return utils.sumBy(excludedItems.value?.excludedOtherAssets, 'subtotal')
    }),
    totalExclusions: computed(() => {
      return grandTotals.excludedRealEstate.value + grandTotals.excludedLiquidAssets.value + grandTotals.excludedOtherAssets.value
    }),
    //----------------------------------
    // Other Totals
    //----------------------------------
    grossTotalAssets: computed(() => {
      return grandTotals.netTotalAssets.value + grandTotals.totalExclusions.value
    }),
    costsAndClaims: computed(() => {
      if(!model.costsAndClaims) {
        return 0
      }
      // Sum only currency entries; rate rows (e.g. state_tax_rate) are not dollar
      // amounts — they feed stateInheritanceTax, which is added separately below.
      const currencyEntries = utils.filter(model.costsAndClaims, (entry) => entry.format == 'currency')
      return utils.sum([
          utils.sumBy(currencyEntries, 'val'),
          costsAndClaimsCalcs.attorneyAdminFees.value,
          costsAndClaimsCalcs.fedEstateTaxes.value,
          costsAndClaimsCalcs.stateInheritanceTax.value,
      ])
    }),
    //----------------------------------
    // Secured/Unsecured Debts
    //----------------------------------
    securedDebt: computed(() => {
      const items = allDebtItems.value?.secured.flatMap((group) => group.items)
      // console.log('securedDebt', items)
      return utils.sumBy(items, 'val') 
    }),
    unsecuredDebt: computed(() => {
      const items = allDebtItems.value?.unsecured.flatMap((group) => group.items)
      // console.log('unsecuredDebt', items)
      return utils.sumBy(items, 'val') 
    }),
  }

  // --------------------------------
  // Costs & Claims Calculations
  // --------------------------------
  function calculateStatutoryFee(base) {
    const tiers = [
      { limit: 100_000,    rate: 0.04  },
      { limit: 100_000,    rate: 0.03  },
      { limit: 800_000,    rate: 0.02  },
      { limit: 9_000_000,  rate: 0.01  },
      { limit: 15_000_000, rate: 0.005 },
    ]
    let remaining = Math.max(0, base || 0)
    let fee = 0
    for (const tier of tiers) {
      const taxable = Math.min(remaining, tier.limit)
      fee += taxable * tier.rate
      remaining -= taxable
      if (remaining <= 0) break
    }
    return fee
  }

  // const FED_TAX_EXEMPTION = {
  //   single:   13610000,
  //   married:  27220000,
  // }
  const costsAndClaimsCalcs = {
    taxExemptionAmount: computed(() => {
      if (!model?.taxExemptionType || !model?.taxExemptionBase) return 0
      return model.taxExemptionBase[model.taxExemptionType]
    }),
    claimsAndFees: computed(() => {
      return utils.sum([
        costsAndClaimsCalcs.attorneyAdminFees.value +
        costsAndClaimsHash.value?.extraAttorneyFees?.val +
        costsAndClaimsHash.value?.creditorClaims?.val
      ])
    }),
    taxableAmount: computed(() => {
      return costsAndClaimsCalcs.statutoryFeeBase.value
    }),
    fedEstateTaxes: computed(() => {
      // console.log('fedEstateTaxes', taxableAmount, 'claimsAndFees', claimsAndFees.value, 'taxExemptionAmount', taxExemptionAmount)
      const ta = costsAndClaimsCalcs.taxableAmount.value
      const te = costsAndClaimsCalcs.taxExemptionAmount.value
      if (ta > te) {
        return 0.4 * (ta - te)
      }
      return 0
    }),
    stateTaxRate: computed(() => costsAndClaimsHash.value?.stateTaxRate?.val),
    stateInheritanceTax: computed(() => {
      const taxRate   = costsAndClaimsCalcs.stateTaxRate.value
      const taxAmount = costsAndClaimsCalcs.taxableAmount.value
      // console.log('stateInheritanceTax', taxRate, taxAmount)
      return !taxRate ? 0 : ((taxRate/100) * taxAmount)
    }),
    // Debt Entries are items where user enters a dollar amount
    debtEntries: computed(() => {
      const itemKeys = [
        "extra_attorney_fees",
        "creditor_claims",
        "income_taxes",
        "estate_liabilities",
        "judgement_against_estate",
      ]
      // default rows matching itemKeys, plus all custom rows
      return utils.filter(model.costsAndClaims, (entry) => itemKeys.includes(entry.key) || entry.isCustom)
    }),
    // CA Probate §10810 — includes all real estate at listing price + liquid/other incl. excluded
    statutoryFeeBase: computed(() => {
      const liquid    = grandTotals.liquidAssets.value + grandTotals.excludedLiquidAssets.value
      const other     = grandTotals.otherAssets.value  + grandTotals.excludedOtherAssets.value
      const reListing = utils.sumBy(realEstate.value, 'listingPrice')
      return liquid + other + reListing
    }),
    statutoryFee: computed(() => {
      return calculateStatutoryFee(costsAndClaimsCalcs.statutoryFeeBase.value)
    }),
    attorneyAdminFees: computed(() => {
      return 2.0 * costsAndClaimsCalcs.statutoryFee.value
    }),
  }

  const estateDistributionCalcs = {
    prelimDistTotal: computed(() => {
      return utils.sumBy(prelimTxnsInScope.value, 'amount')
    }),
    specBeqTotal: computed(() => {
      return utils.sumBy(bequests.value, 'amount')
    }),
    customEntrySum: computed(() => {
      return utils.sumBy(model.estateDistribution, 'val')
    }),
    grossAvailable: computed(() => {
      return grandTotals.netTotalAssets.value - grandTotals.costsAndClaims.value
    }),
    residualForDist: computed(() => {
      const gross   = estateDistributionCalcs.grossAvailable.value
      const prelim  = estateDistributionCalcs.prelimDistTotal.value
      const bequest = estateDistributionCalcs.specBeqTotal.value
      const custom  = estateDistributionCalcs.customEntrySum.value
      return gross - prelim - bequest - custom
    }),
    sharedResidual: computed(() => {
      const net    = estateDistributionCalcs.residualForDist.value
      const prelim = estateDistributionCalcs.prelimDistTotal.value
      return net + prelim
    }),
  }

  // Example Table with Professional Labels
  // Using the scenario from before, here is how a Probate Law Center might label those "Remaining" values:
  //
  // Field Name         Action                   Professional Label                Value (example)
  // grossTotalAssets   Initial Valuation        Inventory Value                   $500,000
  //                    After Debts/Gifts        Residuary Estate                  $450,000
  //                    Adjustment for Advances  Adjusted Hotchpot Balance         $500,000
  //                    Final Payout             Property on Hand for Distribution $450,000*
  // 
  // *Note: The "Property on Hand" is $450k because $50k was already distributed as a preliminary payment.


  return {
    // raw data refs
    model,
    realEstate,
    otherClients,
    members,
    entities,
    bequests,
    estateTransactions,
    prelimTxnsInScope,
    currentAdvanceDate,
    // helper methods
    loadData,
    excludedItems,
    allDebtItems,
    hasExclusions,
    calculateRealEstateProperty,
    // computed calculations
    realEstateCalcs,
    costsAndClaimsCalcs,
    estateDistributionCalcs,
    grandTotals,
    // mapped entries
    liquidAssetsHash,
    costsAndClaimsHash,
    otherAssetsHash,
    // estateDistributionHash,
  }
}, { persist: false }) // Do not persist in localStorage

// id
// estateId
// executorOne
// executorTwo
// liquidAssets
// otherAssets
// costsAndClaims
// estateDistribution
// taxExemptionType
// taxExemptionYear
// createdAt
// updatedAt
